// ============================================================================
// TemplateCard — one program in the list. Shows the name, a compact stat row
// (exercise count + total sets + created date), a short preview of the first
// few exercises with their prescriptions, and edit / delete actions. The whole
// card is clickable to edit; the action buttons stop propagation.
// ============================================================================
import { motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Badge, Button, Card } from "../../components";
import type { Exercise, Template } from "../../api";
import { IconDumbbell, IconTrash } from "./icons";
import { exerciseName, fmtDate, prescription, totalSets } from "./lib";

const PREVIEW_LIMIT = 4;

interface TemplateCardProps {
  template: Template;
  catalog: Map<number, Exercise>;
  onEdit: () => void;
  onDelete: () => void;
}

export function TemplateCard({ template, catalog, onEdit, onDelete }: TemplateCardProps) {
  const { t, i18n } = useTranslation("templates");
  const locale = i18n.language;
  const reduce = useReducedMotion();

  const items = [...(template.items ?? [])].sort(
    (a, b) => (a.ord ?? 0) - (b.ord ?? 0),
  );
  const sets = totalSets(template);
  const preview = items.slice(0, PREVIEW_LIMIT);
  const overflow = items.length - preview.length;

  return (
    <Card
      interactive
      onClick={onEdit}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onEdit();
        }
      }}
      className="flex h-full flex-col focus-visible:outline-none"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate font-display text-lg font-extrabold tracking-tight text-body">
            {template.name?.trim() || t("card.untitled")}
          </h3>
          <p className="mt-0.5 text-xs text-body-faint">
            {t("card.created", { date: fmtDate(template.created_at) })}
          </p>
        </div>
        <span
          className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-coral-soft text-coral"
          aria-hidden
        >
          <IconDumbbell width={20} height={20} />
        </span>
      </div>

      {/* Stat row */}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Badge tone="volt" solid>
          {t("card.items", { count: items.length })}
        </Badge>
        {sets != null && (
          <Badge tone="ember">{t("card.totalSets", { count: sets })}</Badge>
        )}
      </div>

      {/* Preview */}
      <div className="mt-4 flex-1">
        {items.length === 0 ? (
          <p className="text-sm text-body-faint">{t("card.noItems")}</p>
        ) : (
          <ul className="space-y-1.5">
            {preview.map((it, i) => {
              const ex = it.exercise_id != null ? catalog.get(it.exercise_id) : undefined;
              const label = exerciseName(ex, locale) || t("item.unnamed");
              const rx = prescription(it.sets, it.reps, t("item.setsShort"));
              return (
                <motion.li
                  key={it.id ?? `${it.exercise_id}-${i}`}
                  initial={reduce ? false : { opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{
                    duration: 0.3,
                    delay: reduce ? 0 : i * 0.04,
                    ease: [0.22, 0.61, 0.36, 1],
                  }}
                  className="flex items-center justify-between gap-3 text-sm"
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="grid h-5 w-5 shrink-0 place-items-center rounded-pill bg-surface-sunken text-[0.65rem] font-extrabold tabular-nums text-body-soft">
                      {i + 1}
                    </span>
                    <span className="truncate text-body">{label}</span>
                  </span>
                  {rx && (
                    <span className="shrink-0 font-display text-xs font-bold tabular-nums text-body-soft">
                      {rx}
                    </span>
                  )}
                </motion.li>
              );
            })}
            {overflow > 0 && (
              <li className="pt-0.5 text-xs font-bold text-body-faint">
                {t("card.more", { count: overflow })}
              </li>
            )}
          </ul>
        )}
      </div>

      {/* Actions */}
      <div className="mt-5 flex items-center gap-2 border-t border-line pt-4">
        <Button
          size="sm"
          variant="ghost"
          block
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
        >
          {t("actions.edit")}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          aria-label={t("actions.delete")}
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="!px-3 text-body-muted hover:!text-coral"
        >
          <IconTrash width={16} height={16} />
        </Button>
      </div>
    </Card>
  );
}
